'use client';

import { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  $createParagraphNode,
  UNDO_COMMAND,
  REDO_COMMAND,
  CAN_UNDO_COMMAND,
  CAN_REDO_COMMAND,
} from 'lexical';
import {
  $isHeadingNode,
  $createHeadingNode,
  $createQuoteNode,
  $isQuoteNode,
  type HeadingTagType,
} from '@lexical/rich-text';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode,
  ListNode,
} from '@lexical/list';
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link';
import { $getNearestNodeOfType } from '@lexical/utils';
import { $setBlocksType } from '@lexical/selection';
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Code,
  List,
  ListOrdered,
  Link,
  Quote,
  Undo2,
  Redo2,
} from 'lucide-react';

type BlockType = 'paragraph' | 'h1' | 'h2' | 'h3' | 'quote' | 'ul' | 'ol';

export function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isStrikethrough, setIsStrikethrough] = useState(false);
  const [isCode, setIsCode] = useState(false);
  const [isLink, setIsLink] = useState(false);
  const [blockType, setBlockType] = useState<BlockType>('paragraph');
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));

      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();

      if ($isHeadingNode(element)) {
        setBlockType(element.getTag() as BlockType);
      } else if ($isListNode(element)) {
        setBlockType(element.getListType() === 'number' ? 'ol' : 'ul');
      } else {
        const parentList = $getNearestNodeOfType(anchorNode, ListNode);
        if (parentList) {
          setBlockType(parentList.getListType() === 'number' ? 'ol' : 'ul');
        } else if ($isQuoteNode(element)) {
          setBlockType('quote');
        } else {
          setBlockType('paragraph');
        }
      }

      const node = selection.anchor.getNode();
      const parent = node.getParent();
      setIsLink($isLinkNode(parent) || $isLinkNode(node));
    }
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        updateToolbar();
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  useEffect(() => {
    return editor.registerCommand(
      CAN_UNDO_COMMAND,
      (payload) => {
        setCanUndo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor]);

  useEffect(() => {
    return editor.registerCommand(
      CAN_REDO_COMMAND,
      (payload) => {
        setCanRedo(payload);
        return false;
      },
      COMMAND_PRIORITY_CRITICAL,
    );
  }, [editor]);

  const formatHeading = (heading: HeadingTagType) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType === heading) {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createHeadingNode(heading));
        }
      }
    });
  };

  const formatQuote = () => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        if (blockType === 'quote') {
          $setBlocksType(selection, () => $createParagraphNode());
        } else {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      }
    });
  };

  const formatBulletList = () => {
    if (blockType !== 'ul') {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== 'ol') {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
    }
  };

  const insertLink = () => {
    if (!isLink) {
      const url = prompt('Enter URL:');
      if (url) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
      }
    } else {
      editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
    }
  };

  return (
    <div className="lexical-toolbar">
      {/* Undo / Redo */}
      <button
        type="button"
        className="lexical-toolbar-btn"
        disabled={!canUndo}
        onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
        title="Undo (⌘Z)"
        aria-label="Undo"
        style={{ opacity: canUndo ? 1 : 0.4 }}
      >
        <Undo2 size={16} />
      </button>
      <button
        type="button"
        className="lexical-toolbar-btn"
        disabled={!canRedo}
        onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
        title="Redo (⌘⇧Z)"
        aria-label="Redo"
        style={{ opacity: canRedo ? 1 : 0.4 }}
      >
        <Redo2 size={16} />
      </button>

      <div className="lexical-toolbar-divider" />

      {/* Block type select */}
      <select
        className="lexical-toolbar-select"
        value={blockType}
        onChange={(e) => {
          const value = e.target.value as BlockType;
          if (value === 'h1' || value === 'h2' || value === 'h3') {
            formatHeading(value);
          } else if (value === 'ul') {
            formatBulletList();
          } else if (value === 'ol') {
            formatNumberedList();
          } else if (value === 'quote') {
            formatQuote();
          } else {
            editor.update(() => {
              const selection = $getSelection();
              if ($isRangeSelection(selection)) {
                $setBlocksType(selection, () => $createParagraphNode());
              }
            });
          }
        }}
      >
        <option value="paragraph">Normal</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
        <option value="quote">Quote</option>
      </select>

      <div className="lexical-toolbar-divider" />

      {/* Text format buttons */}
      <button
        type="button"
        className={`lexical-toolbar-btn ${isBold ? 'active' : ''}`}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        title="Bold (⌘B)"
        aria-label="Bold"
      >
        <Bold size={16} />
      </button>
      <button
        type="button"
        className={`lexical-toolbar-btn ${isItalic ? 'active' : ''}`}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        title="Italic (⌘I)"
        aria-label="Italic"
      >
        <Italic size={16} />
      </button>
      <button
        type="button"
        className={`lexical-toolbar-btn ${isUnderline ? 'active' : ''}`}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        title="Underline (⌘U)"
        aria-label="Underline"
      >
        <Underline size={16} />
      </button>
      <button
        type="button"
        className={`lexical-toolbar-btn ${isStrikethrough ? 'active' : ''}`}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
        title="Strikethrough"
        aria-label="Strikethrough"
      >
        <Strikethrough size={16} />
      </button>
      <button
        type="button"
        className={`lexical-toolbar-btn ${isCode ? 'active' : ''}`}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code')}
        title="Inline Code"
        aria-label="Inline Code"
      >
        <Code size={16} />
      </button>

      <div className="lexical-toolbar-divider" />

      {/* List buttons */}
      <button
        type="button"
        className={`lexical-toolbar-btn ${blockType === 'ul' ? 'active' : ''}`}
        onClick={formatBulletList}
        title="Bullet List"
        aria-label="Bullet List"
      >
        <List size={16} />
      </button>
      <button
        type="button"
        className={`lexical-toolbar-btn ${blockType === 'ol' ? 'active' : ''}`}
        onClick={formatNumberedList}
        title="Numbered List"
        aria-label="Numbered List"
      >
        <ListOrdered size={16} />
      </button>

      {/* Quote */}
      <button
        type="button"
        className={`lexical-toolbar-btn ${blockType === 'quote' ? 'active' : ''}`}
        onClick={formatQuote}
        title="Block Quote"
        aria-label="Block Quote"
      >
        <Quote size={16} />
      </button>

      <div className="lexical-toolbar-divider" />

      {/* Link */}
      <button
        type="button"
        className={`lexical-toolbar-btn ${isLink ? 'active' : ''}`}
        onClick={insertLink}
        title="Insert Link"
        aria-label="Insert Link"
      >
        <Link size={16} />
      </button>
    </div>
  );
}
